import { ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import { TreeNode } from "utils/tree";

import { STICKY_STACK_MIN_VISIBLE_ROWS, STICKY_TOP_BASE_PX } from "./constants";

const getNodeLevel = (node: { path: string[] }) => node.path.length - 1;

/**
 * Depth of the deepest group held at its slot right now — the bottom of the pinned stack.
 *
 * A group stops moving once the rows above it that aren't its ancestors have scrolled past the fold,
 * and `groupIndex - level` counts exactly those rows. Groups below the fold count too: a chain of
 * single-child groups has nothing but ancestors above it, so the whole chain pins at once.
 */
export const getMaxStickyLevel = (
  stickyGroups: Map<number, number>,
  foldIndex: number,
  nodes: { path: string[] }[]
): number => {
  let maxLevel = -1;

  for (const groupIndex of stickyGroups.keys()) {
    const level = getNodeLevel(nodes[groupIndex]);

    if (groupIndex - level <= foldIndex) {
      maxLevel = Math.max(maxLevel, level);
    }
  }

  return maxLevel;
};

/**
 * Whether the pinned stack still leaves usable room in the scroll container. One row-height per
 * level plus `STICKY_STACK_MIN_VISIBLE_ROWS` of headroom: a stack allowed to reach the container's
 * bottom edge hides the very rows it is context for.
 *
 * A not-yet-measured (zero-height) container counts as fitting, so pinning isn't disabled on the
 * first render before the table is measured.
 */
export const stickyStackFitsContainer = (
  maxStickyLevel: number,
  containerHeight: number
): boolean => {
  const stackBottom = STICKY_TOP_BASE_PX + (maxStickyLevel + 1) * ROW_HEIGHT_ESTIMATE;
  const requiredHeight = stackBottom + STICKY_STACK_MIN_VISIBLE_ROWS * ROW_HEIGHT_ESTIMATE;

  return containerHeight === 0 || requiredHeight <= containerHeight;
};

// Binary search: index of the first row whose bottom edge is below `offset`, or the last index
// when every row ends above it.
export const findFirstRowBelow = (
  measurements: readonly { end: number }[],
  offset: number
): number => {
  let low = 0;
  let high = measurements.length - 1;
  let result = high;

  while (low <= high) {
    const mid = (low + high) >> 1;

    if (measurements[mid].end > offset) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return result;
};

/**
 * For each expanded group with visible descendants, returns `[groupIndex, lastDescendantIndex]`
 * into the flat `visibleNodes` array (sorted by group index). Single pass, O(n): an ancestor stack
 * holds the open groups, and a group closes — its last descendant being the previous row — as soon
 * as a row at its level or shallower appears (depth-first order makes a subtree contiguous).
 */
export const buildStickyRanges = <T>(nodes: TreeNode<T>[]): [number, number][] => {
  const ranges: [number, number][] = [];
  const openGroups: { index: number; level: number }[] = [];

  const closeGroup = (lastDescendant: number) => {
    const group = openGroups.pop();

    if (group !== undefined && lastDescendant > group.index) {
      ranges.push([group.index, lastDescendant]);
    }
  };

  for (let i = 0; i < nodes.length; i++) {
    const level = getNodeLevel(nodes[i]);

    while (openGroups.length > 0 && openGroups[openGroups.length - 1].level >= level) {
      closeGroup(i - 1);
    }

    if (nodes[i].children.length > 0) {
      openGroups.push({ index: i, level });
    }
  }

  while (openGroups.length > 0) {
    closeGroup(nodes.length - 1);
  }

  return ranges.sort((a, b) => a[0] - b[0]);
};

// Like the shared `calculateVirtualPadding`, but discounts the sticky ancestor rows that are
// force-rendered (stacked) above the visible window.
export const calculateTreeVirtualPadding = (
  virtualizedItems: { start: number; size: number; end: number }[],
  totalSize: number,
  scrollMargin = 0,
  forcedStickyCount = 0
): [number, number] => {
  if (!virtualizedItems.length) {
    return [0, 0];
  }

  const firstRealIndex = Math.min(forcedStickyCount, virtualizedItems.length - 1);

  let forcedSize = 0;
  for (let i = 0; i < firstRealIndex; i++) {
    forcedSize += virtualizedItems[i].size;
  }

  const before = virtualizedItems[firstRealIndex].start - scrollMargin - forcedSize;
  const after = totalSize - virtualizedItems[virtualizedItems.length - 1].end;

  return [Math.max(0, before), Math.max(0, after)];
};
